import { useEffect, useState } from "react";

// Phase F3 — single source of truth for navigator.onLine.
//
// Subscribes to the browser's `online`/`offline` events so any consumer
// re-renders when the network state flips. SSR-safe (defaults to true
// when window is undefined). Multiple consumers can use this hook
// simultaneously — each adds its own pair of listeners; React batches
// the resulting state updates.
export function useOnlineStatus(): {
  isOnline: boolean;
  isOffline: boolean;
} {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return { isOnline, isOffline: !isOnline };
}
